import type { GeneratedPackageSettings } from '@ms-cloudpack/common-types';
import { checkMatch, toMatchObject } from './checkMatch.js';
import { getMostSpecificMatchingRange } from './getMostSpecificMatchingRange.js';

/**
 * Get the first matching generated package settings for this package name and version.
 * This will return the original object which currently is mutated to update the settings.
 *
 * To get all matching user and generated package settings, use `getPackageSettings` instead.
 */
export function getGeneratedPackageSettings(params: {
  name: string;
  version: string;
  generatedPackageSettings: GeneratedPackageSettings[] | undefined;
}): GeneratedPackageSettings | undefined {
  const { name, version, generatedPackageSettings } = params;
  const allMatches = generatedPackageSettings?.filter(({ match }) =>
    checkMatch({ name, version, match, exactMatch: true }),
  );

  if (!allMatches) {
    return undefined;
  }

  // If there is only one match, return it.
  if (allMatches.length == 1) {
    return allMatches[0];
  }

  // If there are multiple matches, we need to find the one with the minimum satisfying version range.
  // This is needed to ensure that we get the most specific match.

  // Extract all version ranges from those matches
  const versionRanges = allMatches.map((s) => toMatchObject(s.match).version).filter(Boolean) as string[];

  // Now determine the most specific matching range
  const mostSpecificRange = getMostSpecificMatchingRange(version, versionRanges);

  // It is impossible have undefined in this case, so this should never happen.
  if (!mostSpecificRange) {
    return undefined;
  }

  // Now find the match with the most specific matching range
  return allMatches.find(({ match }) => toMatchObject(match).version === mostSpecificRange);
}
